import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { GUIDES } from "../content/guides";
import { CALCULATORS } from "../content/calculators";
import { useSeo, useJsonLd, SITE_URL } from "../lib/seo";
import NotFound from "./NotFound";

export default function GuideArticle() {
  const { slug } = useParams();
  const guide = GUIDES[slug];

  useSeo({
    title: guide ? `${guide.title} | ProfitMigo Guides` : "Guide | ProfitMigo",
    description: guide?.description,
    path: guide ? `/guides/${guide.slug}` : "/guides",
  });

  useJsonLd("ld-guide", guide ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: guide.title,
        description: guide.description,
        dateModified: guide.updated,
        author: { "@type": "Organization", name: "ProfitMigo" },
        publisher: { "@type": "Organization", name: "ProfitMigo" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "Guides", item: SITE_URL + "/guides" },
          { "@type": "ListItem", position: 3, name: guide.title, item: SITE_URL + "/guides/" + guide.slug },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: guide.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  } : null);

  if (!guide) return <NotFound />;

  return (
    <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Guides", to: "/guides" }, { label: guide.title }]} />
      <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">{guide.title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">Updated {guide.updated}</p>

      <div className="mt-6 space-y-4 text-lg leading-relaxed text-foreground/80">
        {guide.intro.map((p, i) => <p key={i}>{p}</p>)}
      </div>

      {guide.sections.map((s, i) => (
        <section key={i} className="mt-8">
          <h2 className="font-display text-2xl font-semibold tracking-tight">{s.h2}</h2>
          <div className="mt-3 space-y-3 text-base leading-relaxed text-foreground/80">
            {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
          </div>
        </section>
      ))}

      <div className="mt-10 rounded-xl border border-border bg-accent/40 p-6">
        <h2 className="font-display text-lg font-semibold">Key takeaways</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {guide.keyTakeaways.map((k, i) => <li key={i} className="flex gap-2"><span className="text-primary">→</span>{k}</li>)}
        </ul>
      </div>

      <section className="mt-10">
        <h2 className="font-display text-xl font-semibold">Try the calculators</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {guide.relatedCalculators.map((cs) => {
            const c = CALCULATORS[cs];
            if (!c) return null;
            return (
              <Link key={cs} to={c.path} data-testid={`guide-calc-${cs}`} className="group flex items-center justify-between rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary">
                <span className="font-medium">{c.name}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
              </Link>
            );
          })}
        </div>
      </section>

      {guide.faq.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold">FAQ</h2>
          <div className="mt-4 space-y-4">
            {guide.faq.map((f, i) => (
              <div key={i}>
                <div className="font-medium">{f.q}</div>
                <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
